// src/store/useStore.js - LENGKAP DENGAN MULTI-METHOD SUPPORT (DIPERBAIKI)
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';
import socketService from '../services/socketService';

// Selector helpers
export const selectUser = (state) => state.user;
export const selectIsAuthenticated = (state) => state.isAuthenticated;
export const selectVessels = (state) => state.vessels;
export const selectStats = (state) => state.stats;
export const selectCompanies = (state) => state.companies;
export const selectUserCompany = (state) => state.userCompany;
export const selectAnomalies = (state) => state.anomalies;
export const selectTrips = (state) => state.trips;
export const selectFuelData = (state) => state.fuelData;
export const selectRealtimeData = (state) => state.realtimeData;
export const selectMultiMethodData = (state) => state.multiMethodData;

const useStore = create(
  persist(
    (set, get) => ({
      // ==================== AUTH STATE ====================
      user: null,
      token: null,
      isAuthenticated: false,
      isInitialized: false,
      isLoading: false,
      userCompany: null,

      // ==================== DATA STATE ====================
      vessels: [],
      companies: [],
      users: [],
      trips: [],
      fuelData: [],
      anomalies: [],
      multiMethodData: null,
      realtimeData: {
        consumption: 0,
        distance: 0,
        efficiency: 0,
        isActive: false,
        latestReadings: null
      },
      stats: {
        totalVessels: 0,
        activeVessels: 0,
        totalFuelConsumption: 0,
        averageEfficiency: 0,
        totalDistance: 0,
        anomalyCount: 0,
      },

      // ==================== UI STATE ====================
      darkMode: true,
      sidebarCollapsed: false,
      wsConnected: false,

      // Flag untuk mencegah multiple fetch
      _isFetchingCompanies: false,
      _initialDataLoaded: false,

      // Cache untuk menghindari fetch berulang
      _lastFetchTimes: {
        vessels: 0,
        stats: 0,
        anomalies: 0,
        trips: 0,
        fuelData: 0,
        companies: 0,
        multiMethod: 0,
      },
      _cacheTTL: 30000,

      // ==================== INITIALIZATION ====================
      initializeApp: async () => {
        if (get()._initialDataLoaded) {
          console.log('App already initialized, skipping...');
          return;
        }

        set({ isLoading: true });
        try {
          const token = localStorage.getItem('token');
          
          if (token) {
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            
            try {
              const response = await api.get('/auth/me');
              const userData = response.data?.data || response.data;
              
              if (userData && (userData.id || userData._id)) {
                let companyData = userData.company;
                if (companyData && typeof companyData === 'object' && companyData._id) {
                  set({ userCompany: companyData });
                } else if (userData.companyId) {
                  try {
                    const companyRes = await api.get(`/companies/${userData.companyId}`);
                    const fetchedCompany = companyRes.data?.data || companyRes.data;
                    set({ userCompany: fetchedCompany });
                  } catch (err) {
                    console.log('Company fetch error:', err);
                    set({ userCompany: { _id: userData.companyId, name: 'Company' } });
                  }
                }
                
                set({
                  user: userData,
                  isAuthenticated: true,
                });
                
                await get().loadInitialData();
              }
            } catch (authError) {
              console.error('Auth error:', authError);
              localStorage.removeItem('token');
              delete api.defaults.headers.common['Authorization'];
            }
          }

          // Initialize WebSocket
          socketService.init();
          socketService.on('connect', () => {
            set({ wsConnected: true });
          });
          socketService.on('disconnect', () => {
            set({ wsConnected: false });
          });
          socketService.on('sensor_update', (data) => {
            get().handleSensorUpdate(data);
          });
          socketService.on('fleet_update', (data) => {
            get().handleFleetUpdate(data);
          });

          set({ isInitialized: true, isLoading: false, _initialDataLoaded: true });
        } catch (error) {
          console.error('Init error:', error);
          set({ isInitialized: true, isLoading: false, _initialDataLoaded: true });
        }
      },

      loadInitialData: async () => {
        const now = Date.now();
        try {
          const user = get().user;
          
          if (user?.role === 'super_admin' && get().companies.length === 0) {
            await get().fetchCompanies(true);
          }
          
          await Promise.all([
            get().fetchDashboardStats(true),
            get().fetchVessels(true),
            get().fetchAnomalies({ limit: 10 }, true),
            get().fetchTrips({ limit: 5 }, true),
            get().fetchFuelData({ range: 'week' }, true),
            get().fetchRealtimeConsumption()
          ]);
          
          set((state) => ({
            _lastFetchTimes: {
              ...state._lastFetchTimes,
              vessels: now,
              stats: now,
              anomalies: now,
              trips: now,
              fuelData: now,
            }
          }));
        } catch (error) {
          console.error('Initial data load error:', error);
        }
      },

      getFilteredVessels: () => {
        const { vessels, user, userCompany } = get();
        if (user?.role === 'super_admin') {
          return vessels;
        }
        const companyId = user?.companyId || userCompany?._id;
        if (companyId) {
          return vessels.filter(v => v.companyId === companyId || v.companyId?._id === companyId);
        }
        return [];
      },

      getVesselsByCompany: () => {
        const { vessels, companies, user } = get();
        if (user?.role !== 'super_admin') {
          return [];
        }
        
        const companyMap = new Map();
        
        companies.forEach(company => {
          companyMap.set(company._id, {
            companyId: company._id,
            companyName: company.name,
            companyCode: company.code,
            vessels: [],
            stats: {
              totalVessels: 0,
              activeVessels: 0,
              totalFuel: 0,
              totalDistance: 0,
              avgEfficiency: 0,
              anomalyCount: 0
            }
          });
        });
        
        vessels.forEach(vessel => {
          const companyId = vessel.companyId?._id || vessel.companyId;
          if (companyMap.has(companyId)) {
            companyMap.get(companyId).vessels.push(vessel);
          } else if (companyId) {
            companyMap.set(companyId, {
              companyId: companyId,
              companyName: vessel.companyName || 'Unknown',
              companyCode: '',
              vessels: [vessel],
              stats: {
                totalVessels: 0,
                activeVessels: 0,
                totalFuel: 0,
                totalDistance: 0,
                avgEfficiency: 0,
                anomalyCount: 0
              }
            });
          }
        });
        
        for (const [_, companyData] of companyMap) {
          companyData.stats.totalVessels = companyData.vessels.length;
          companyData.stats.activeVessels = companyData.vessels.filter(v => v.status === 'active').length;
        }
        
        return Array.from(companyMap.values());
      },

      // ==================== WEB SOCKET HANDLERS ====================
      handleSensorUpdate: (data) => {
        const { vessels } = get();
        if (data.vesselId && vessels.length > 0) {
          const vesselIndex = vessels.findIndex(v => v._id === data.vesselId);
          if (vesselIndex !== -1) {
            const updatedVessels = [...vessels];
            updatedVessels[vesselIndex] = {
              ...updatedVessels[vesselIndex],
              lastDataReceived: data.timestamp,
              lastSensorData: data.data,
              lastLocation: data.position ? {
                lat: data.position.lat,
                lng: data.position.lng,
                speed: data.position.speed,
                heading: data.position.heading,
                timestamp: data.timestamp
              } : updatedVessels[vesselIndex].lastLocation
            };
            set({ vessels: updatedVessels });
            
            if (data.fuel) {
              get().fetchRealtimeConsumption();
            }
          }
        }
      },

      handleFleetUpdate: (data) => {
        const { vessels } = get();
        if (data.vesselId && data.position) {
          const vesselIndex = vessels.findIndex(v => v._id === data.vesselId);
          if (vesselIndex !== -1) {
            const updatedVessels = [...vessels];
            updatedVessels[vesselIndex] = {
              ...updatedVessels[vesselIndex],
              lastLocation: {
                lat: data.position.lat,
                lng: data.position.lng,
                speed: data.position.speed,
                heading: data.position.heading,
                timestamp: data.timestamp
              }
            };
            set({ vessels: updatedVessels });
          }
        }
      },

      // ==================== AUTHENTICATION ====================
      login: async (username, password) => {
        set({ isLoading: true });
        try {
          const response = await api.post('/auth/login', { username, password });
          
          const responseData = response.data?.data || response.data;
          const { token, user } = responseData;

          if (!token || !user) {
            throw new Error('Invalid response from server');
          }

          localStorage.setItem('token', token);
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

          let companyData = user.company;
          if (companyData && typeof companyData === 'object' && companyData._id) {
            set({ userCompany: companyData });
          } else if (user.companyId) {
            try {
              const companyRes = await api.get(`/companies/${user.companyId}`);
              const fetchedCompany = companyRes.data?.data || companyRes.data;
              set({ userCompany: fetchedCompany });
            } catch (err) {
              set({ userCompany: { _id: user.companyId, name: 'Company' } });
            }
          }

          set({ user, token, isAuthenticated: true, isLoading: false });

          socketService.connect();
          
          set({ _initialDataLoaded: false });
          await get().loadInitialData();

          return user;
        } catch (error) {
          console.error('Login error:', error);
          set({ isLoading: false });
          throw error;
        }
      },

      logout: () => {
        localStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
        socketService.disconnect();
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          userCompany: null,
          vessels: [],
          companies: [],
          users: [],
          trips: [],
          fuelData: [],
          anomalies: [],
          multiMethodData: null,
          realtimeData: {
            consumption: 0,
            distance: 0,
            efficiency: 0,
            isActive: false,
            latestReadings: null
          },
          stats: {
            totalVessels: 0,
            activeVessels: 0,
            totalFuelConsumption: 0,
            averageEfficiency: 0,
            totalDistance: 0,
            anomalyCount: 0,
          },
          _initialDataLoaded: false,
          _isFetchingCompanies: false,
        });
      },

      updateProfile: async (data) => {
        try {
          const response = await api.put('/auth/profile', data);
          const userData = response.data?.data || response.data;
          set({ user: userData });
          return userData;
        } catch (error) {
          throw error;
        }
      },

      changePassword: async (data) => {
        try {
          const response = await api.put('/auth/change-password', data);
          return response.data;
        } catch (error) {
          throw error;
        }
      },

      // ==================== DASHBOARD STATS ====================
      fetchDashboardStats: async (force = false) => {
        const now = Date.now();
        const lastFetch = get()._lastFetchTimes?.stats || 0;
        
        if (!force && (now - lastFetch) < get()._cacheTTL) {
          return get().stats;
        }
        
        try {
          const response = await api.get('/dashboard/stats');
          const statsData = response.data?.data || response.data || {};
          set({ stats: statsData });
          set((state) => ({ 
            _lastFetchTimes: { ...state._lastFetchTimes, stats: now } 
          }));
          return statsData;
        } catch (error) {
          console.error('Fetch stats error:', error);
          return null;
        }
      },

      fetchDashboardStatsRealtime: async (params = {}) => {
        try {
          const response = await api.get('/fuel/dashboard-stats', { params });
          const statsData = response.data?.data || response.data || {};
          set({ stats: statsData });
          return statsData;
        } catch (error) {
          console.error('Fetch realtime stats error:', error);
          return null;
        }
      },

      // ==================== VESSEL MANAGEMENT ====================
      fetchVessels: async (force = false) => {
        const now = Date.now();
        const lastFetch = get()._lastFetchTimes?.vessels || 0;
        
        if (!force && (now - lastFetch) < get()._cacheTTL && get().vessels.length > 0) {
          return get().vessels;
        }
        
        try {
          const response = await api.get('/vessels');
          const vesselsData = response.data?.data || response.data || [];
          
          set({ vessels: vesselsData });
          set((state) => ({ 
            _lastFetchTimes: { ...state._lastFetchTimes, vessels: now } 
          }));
          return vesselsData;
        } catch (error) {
          console.error('Fetch vessels error:', error);
          set({ vessels: [] });
          return [];
        }
      },

      getVesselById: async (id) => {
        const cached = get().vessels.find(v => v._id === id);
        if (cached) return cached;
        
        try {
          const response = await api.get(`/vessels/${id}`);
          return response.data?.data || response.data;
        } catch (error) {
          console.error('Get vessel error:', error);
          return null;
        }
      },

      createVessel: async (vesselData) => {
        try {
          const response = await api.post('/vessels', vesselData);
          const newVessel = response.data?.data || response.data;
          set((state) => ({ vessels: [...state.vessels, newVessel] }));
          set((state) => ({ _lastFetchTimes: { ...state._lastFetchTimes, vessels: 0 } }));
          await get().fetchDashboardStats(true);
          return newVessel;
        } catch (error) {
          console.error('Create vessel error:', error);
          throw error;
        }
      },

      updateVessel: async (id, vesselData) => {
        try {
          const response = await api.put(`/vessels/${id}`, vesselData);
          const updatedVessel = response.data?.data || response.data;
          set((state) => ({
            vessels: state.vessels.map(v => v._id === id ? updatedVessel : v),
          }));
          set((state) => ({ _lastFetchTimes: { ...state._lastFetchTimes, vessels: 0 } }));
          return updatedVessel;
        } catch (error) {
          console.error('Update vessel error:', error);
          throw error;
        }
      },

      deleteVessel: async (id) => {
        try {
          await api.delete(`/vessels/${id}`);
          set((state) => ({
            vessels: state.vessels.filter(v => v._id !== id),
          }));
          set((state) => ({ _lastFetchTimes: { ...state._lastFetchTimes, vessels: 0 } }));
          await get().fetchDashboardStats(true);
          return true;
        } catch (error) {
          console.error('Delete vessel error:', error);
          throw error;
        }
      },

      updateVesselSensors: async (id, sensors) => {
        try {
          const response = await api.put(`/vessels/${id}/sensors`, { sensors });
          const updatedSensors = response.data?.data || response.data;
          
          set((state) => ({
            vessels: state.vessels.map(v => 
              v._id === id 
                ? { ...v, sensors: updatedSensors }
                : v
            ),
          }));
          
          set((state) => ({ _lastFetchTimes: { ...state._lastFetchTimes, vessels: 0 } }));
          return updatedSensors;
        } catch (error) {
          console.error('Update vessel sensors error:', error);
          throw error;
        }
      },

      // API Key Management
      generateApiKey: async (id) => {
        try {
          const response = await api.post(`/vessels/${id}/generate-api-key`);
          const result = response.data?.data || response.data;
          
          set((state) => ({
            vessels: state.vessels.map(v => 
              v._id === id 
                ? { ...v, apiKey: result.apiKey, apiToken: result.apiToken }
                : v
            ),
          }));
          
          return result;
        } catch (error) {
          console.error('Generate API key error:', error);
          throw error;
        }
      },

      rotateApiKey: async (id) => {
        try {
          const response = await api.post(`/vessels/${id}/rotate-api-key`);
          const result = response.data?.data || response.data;
          
          set((state) => ({
            vessels: state.vessels.map(v => 
              v._id === id 
                ? { ...v, apiKey: result.apiKey, apiToken: result.apiToken }
                : v
            ),
          }));
          
          return result;
        } catch (error) {
          console.error('Rotate API key error:', error);
          throw error;
        }
      },

      revokeApiKey: async (id) => {
        try {
          await api.delete(`/vessels/${id}/revoke-api-key`);
          
          set((state) => ({
            vessels: state.vessels.map(v => 
              v._id === id 
                ? { ...v, apiKey: undefined, apiToken: undefined }
                : v
            ),
          }));
          
          return true;
        } catch (error) {
          console.error('Revoke API key error:', error);
          throw error;
        }
      },

      getApiStatus: async (id) => {
        try {
          const response = await api.get(`/vessels/${id}/api-status`);
          return response.data?.data || response.data;
        } catch (error) {
          console.error('Get API status error:', error);
          return null;
        }
      },

      // ==================== COMPANY MANAGEMENT ====================
      fetchCompanies: async (force = false) => {
        const user = get().user;
        
        if (user?.role !== 'super_admin') {
          return [];
        }
        
        if (get()._isFetchingCompanies) {
          console.log('Companies fetch already in progress, skipping...');
          return get().companies;
        }
        
        const now = Date.now();
        const lastFetch = get()._lastFetchTimes?.companies || 0;
        
        if (!force && (now - lastFetch) < get()._cacheTTL && get().companies.length > 0) {
          return get().companies;
        }
        
        set({ _isFetchingCompanies: true });
        
        try {
          const response = await api.get('/companies');
          const companiesData = response.data?.data || response.data || [];
          set({ companies: companiesData });
          set((state) => ({ 
            _lastFetchTimes: { ...state._lastFetchTimes, companies: now } 
          }));
          return companiesData;
        } catch (error) {
          console.error('Fetch companies error:', error);
          return [];
        } finally {
          set({ _isFetchingCompanies: false });
        }
      },

      createCompany: async (data) => {
        try {
          const response = await api.post('/companies', data);
          const newCompany = response.data?.data || response.data;
          set((state) => ({ companies: [...state.companies, newCompany] }));
          return newCompany;
        } catch (error) {
          console.error('Create company error:', error);
          throw error;
        }
      },

      updateCompany: async (id, data) => {
        try {
          const response = await api.put(`/companies/${id}`, data);
          const updatedCompany = response.data?.data || response.data;
          set((state) => ({
            companies: state.companies.map(c => c._id === id ? updatedCompany : c),
          }));
          return updatedCompany;
        } catch (error) {
          console.error('Update company error:', error);
          throw error;
        }
      },

      deleteCompany: async (id) => {
        try {
          await api.delete(`/companies/${id}`);
          set((state) => ({
            companies: state.companies.filter(c => c._id !== id),
          }));
        } catch (error) {
          console.error('Delete company error:', error);
          throw error;
        }
      },

      // ==================== USER MANAGEMENT ====================
      fetchUsers: async () => {
        try {
          const response = await api.get('/users');
          const usersData = response.data?.data || response.data || [];
          set({ users: usersData });
          return usersData;
        } catch (error) {
          console.error('Fetch users error:', error);
          return [];
        }
      },

      createUser: async (data) => {
        try {
          const response = await api.post('/users', data);
          const newUser = response.data?.data || response.data;
          set((state) => ({ users: [...state.users, newUser] }));
          return newUser;
        } catch (error) {
          console.error('Create user error:', error);
          throw error;
        }
      },

      updateUser: async (id, data) => {
        try {
          const response = await api.put(`/users/${id}`, data);
          const updatedUser = response.data?.data || response.data;
          set((state) => ({
            users: state.users.map(u => u._id === id ? updatedUser : u),
          }));
          return updatedUser;
        } catch (error) {
          console.error('Update user error:', error);
          throw error;
        }
      },

      deleteUser: async (id) => {
        try {
          await api.delete(`/users/${id}`);
          set((state) => ({
            users: state.users.filter(u => u._id !== id),
          }));
        } catch (error) {
          console.error('Delete user error:', error);
          throw error;
        }
      },

      // ==================== TRIPS ====================
      fetchTrips: async (params, force = false) => {
        const now = Date.now();
        const cacheKey = `trips_${JSON.stringify(params)}`;
        const lastFetch = get()._lastFetchTimes?.[cacheKey] || 0;
        
        if (!force && (now - lastFetch) < get()._cacheTTL && get().trips.length > 0) {
          return get().trips;
        }
        
        try {
          const response = await api.get('/trips', { params });
          const tripsData = response.data?.data || response.data || [];
          set({ trips: tripsData });
          set((state) => ({ 
            _lastFetchTimes: { ...state._lastFetchTimes, [cacheKey]: now } 
          }));
          return tripsData;
        } catch (error) {
          console.error('Fetch trips error:', error);
          return [];
        }
      },

      getTripById: async (id) => {
        try {
          const response = await api.get(`/trips/${id}`);
          return response.data?.data || response.data;
        } catch (error) {
          console.error('Get trip error:', error);
          return null;
        }
      },

      getTripPlayback: async (id) => {
        try {
          const response = await api.get(`/trips/${id}/playback`);
          return response.data?.data || response.data;
        } catch (error) {
          console.error('Get playback error:', error);
          return null;
        }
      },

      getActiveTrips: async () => {
        try {
          const response = await api.get('/trips/status/active');
          return response.data?.data || response.data || [];
        } catch (error) {
          console.error('Get active trips error:', error);
          return [];
        }
      },

      // ==================== FUEL DATA ====================
      fetchFuelData: async (params, force = false) => {
        const now = Date.now();
        const cacheKey = `fuelData_${JSON.stringify(params)}`;
        const lastFetch = get()._lastFetchTimes?.[cacheKey] || 0;
        
        if (!force && (now - lastFetch) < get()._cacheTTL && get().fuelData.length > 0) {
          return get().fuelData;
        }
        
        try {
          const response = await api.get('/fuel/consumption', { params });
          const data = response.data?.data || response.data || [];
          set({ fuelData: data });
          set((state) => ({ 
            _lastFetchTimes: { ...state._lastFetchTimes, [cacheKey]: now } 
          }));
          return data;
        } catch (error) {
          console.error('Fetch fuel data error:', error);
          return [];
        }
      },

      fetchAnomalies: async (params, force = false) => {
        const now = Date.now();
        const cacheKey = `anomalies_${JSON.stringify(params)}`;
        const lastFetch = get()._lastFetchTimes?.[cacheKey] || 0;
        
        if (!force && (now - lastFetch) < get()._cacheTTL && get().anomalies.length > 0) {
          return get().anomalies;
        }
        
        try {
          const apiParams = {};
          if (params.vesselId) apiParams.vesselId = params.vesselId;
          if (params.limit) apiParams.limit = params.limit;
          else if (params.range) {
            const limitMap = { day: 10, week: 20, month: 50 };
            apiParams.limit = limitMap[params.range] || 50;
          } else {
            apiParams.limit = 50;
          }
          
          const response = await api.get('/fuel/anomalies', { params: apiParams });
          const data = response.data?.data || response.data || [];
          set({ anomalies: data });
          set((state) => ({ 
            _lastFetchTimes: { ...state._lastFetchTimes, [cacheKey]: now } 
          }));
          return data;
        } catch (error) {
          console.error('Fetch anomalies error:', error);
          set({ anomalies: [] });
          return [];
        }
      },

      fetchSpeedTrend: async (params) => {
        try {
          const response = await api.get('/fuel/speed-trend', { params });
          const data = response.data?.data || response.data || [];
          return data;
        } catch (error) {
          console.error('Fetch speed trend error:', error);
          return [];
        }
      },

      fetchVesselComparison: async (params) => {
        try {
          const response = await api.get('/fuel/vessel-comparison', { params });
          const data = response.data?.data || response.data || [];
          return data;
        } catch (error) {
          console.error('Fetch vessel comparison error:', error);
          return [];
        }
      },

      fetchRealtimeConsumption: async (params = {}) => {
        try {
          const response = await api.get('/fuel/consumption/realtime', { params });
          const data = response.data?.data || response.data || {};
          set({ realtimeData: data });
          return data;
        } catch (error) {
          console.error('Fetch realtime consumption error:', error);
          return {};
        }
      },

      // ==================== MULTI-METHOD FUEL (DIPERBAIKI) ====================
      fetchMultiMethodFuel: async (vesselId, range = 'week', startDate = null, endDate = null, tripId = null, force = false) => {
        if (!vesselId) {
          set({ multiMethodData: null });
          return null;
        }
        
        // Buat cache key yang unik berdasarkan parameter
        let cacheKey = `multiMethod_${vesselId}`;
        if (tripId) {
          cacheKey = `multiMethod_${vesselId}_trip_${tripId}`;
        } else if (startDate && endDate) {
          cacheKey = `multiMethod_${vesselId}_${startDate}_${endDate}`;
        } else {
          cacheKey = `multiMethod_${vesselId}_${range}`;
        }
        
        const now = Date.now();
        const lastFetch = get()._lastFetchTimes?.[cacheKey] || 0;
        
        if (!force && (now - lastFetch) < get()._cacheTTL && get().multiMethodData) {
          console.log('📦 Using cached multi-method data');
          return get().multiMethodData;
        }
        
        try {
          // Build params sesuai dengan yang diterima backend
          let params = {};
          if (tripId) {
            params.tripId = tripId;
          } else if (startDate && endDate) {
            params.startDate = startDate;
            params.endDate = endDate;
          } else {
            params.range = range;
          }
          params.vesselId = vesselId;
          
          console.log('📊 fetchMultiMethodFuel params:', params);
          
          const response = await api.get('/fuel/consumption/multi-method', { params });
          const data = response.data?.data || response.data;
          set({ multiMethodData: data });
          set((state) => ({ 
            _lastFetchTimes: { ...state._lastFetchTimes, [cacheKey]: now } 
          }));
          return data;
        } catch (error) {
          console.error('Fetch multi-method fuel error:', error);
          set({ multiMethodData: null });
          return null;
        }
      },

      clearMultiMethodData: () => {
        set({ multiMethodData: null });
      },

      // ==================== SENSOR DATA ====================
      getVesselSensors: async (vesselId) => {
        try {
          const response = await api.get(`/vessels/${vesselId}/sensors`);
          const data = response.data?.data || response.data || [];
          return data;
        } catch (error) {
          console.error('Get sensors error:', error);
          return [];
        }
      },

      getVesselSensorValues: async (vesselId) => {
        try {
          const response = await api.get(`/vessels/${vesselId}/sensors/values`);
          const data = response.data?.data || response.data || {};
          return data;
        } catch (error) {
          console.error('Get sensor values error:', error);
          return {};
        }
      },

      getVesselPosition: async (vesselId) => {
        try {
          const response = await api.get(`/vessels/${vesselId}/position`);
          const data = response.data?.data || response.data || {};
          return data;
        } catch (error) {
          console.error('Get position error:', error);
          return {};
        }
      },

      getVesselHistory: async (vesselId, params) => {
        try {
          const response = await api.get(`/vessels/${vesselId}/history`, { params });
          const data = response.data?.data || response.data || [];
          return data;
        } catch (error) {
          console.error('Get history error:', error);
          return [];
        }
      },

      getVesselGpsHistory: async (vesselId, params) => {
        try {
          const response = await api.get(`/vessels/${vesselId}/gps-history`, { params });
          const data = response.data?.data || response.data || [];
          return data;
        } catch (error) {
          console.error('Get GPS history error:', error);
          return [];
        }
      },

      // ==================== FUEL ESTIMATION ====================
      getFuelEstimation: async (vesselId) => {
        try {
          const response = await api.get(`/vessels/${vesselId}/fuel-estimation`);
          return response.data?.data || response.data;
        } catch (error) {
          console.error('Get fuel estimation error:', error);
          return null;
        }
      },

      updateFuelEstimation: async (vesselId, data) => {
        try {
          const response = await api.put(`/vessels/${vesselId}/fuel-estimation`, data);
          const result = response.data?.data || response.data;
          
          set((state) => ({
            vessels: state.vessels.map(v => 
              v._id === vesselId 
                ? { ...v, fuelEstimation: result }
                : v
            ),
          }));
          
          return result;
        } catch (error) {
          console.error('Update fuel estimation error:', error);
          throw error;
        }
      },

      // ==================== REPORTS ====================
      getFuelReport: async (params) => {
        try {
          const response = await api.get('/reports/fuel', { params });
          return response.data?.data || response.data || {};
        } catch (error) {
          console.error('Get fuel report error:', error);
          return {};
        }
      },

      getTripReport: async (params) => {
        try {
          const response = await api.get('/reports/trips', { params });
          return response.data?.data || response.data || {};
        } catch (error) {
          console.error('Get trip report error:', error);
          return {};
        }
      },

      getPerformanceReport: async (params) => {
        try {
          const response = await api.get('/reports/performance', { params });
          return response.data?.data || response.data || {};
        } catch (error) {
          console.error('Get performance report error:', error);
          return {};
        }
      },

      exportReport: async (type, params) => {
        try {
          const response = await api.get(`/reports/export/${type}`, { params, responseType: 'blob' });
          return response.data;
        } catch (error) {
          console.error('Export report error:', error);
          throw error;
        }
      },

      // ==================== UI ACTIONS ====================
      toggleDarkMode: () => {
        set((state) => ({ darkMode: !state.darkMode }));
        if (!get().darkMode) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      },

      toggleSidebar: () => {
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }));
      },

      setLoading: (isLoading) => set({ isLoading }),

      refreshAllData: async () => {
        await Promise.all([
          get().fetchDashboardStatsRealtime(),
          get().fetchVessels(true),
          get().fetchAnomalies({ limit: 10 }, true),
          get().fetchTrips({ limit: 5 }, true),
          get().fetchFuelData({ range: 'week' }, true),
          get().fetchRealtimeConsumption()
        ]);
        
        // Clear multi-method cache
        set((state) => ({
          _lastFetchTimes: {
            ...state._lastFetchTimes,
            multiMethod: 0
          }
        }));
      }
    }),
    {
      name: 'fuel-monitor-storage',
      partialize: (state) => ({
        darkMode: state.darkMode,
        sidebarCollapsed: state.sidebarCollapsed,
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        userCompany: state.userCompany,
      }),
    }
  )
);

export { useStore };